import { useState, useEffect } from 'react';
import {
  FileText,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Clock,
  Server,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Eye,
  Activity,
} from 'lucide-react';
import {
  imageLogs,
  imageKeys,
  imageBackends,
  ImageRequestLog,
  ImageApiKey,
  ImageBackend,
  ImageRequestFilters,
} from '../../api/client';
import { formatDateTime, cn } from '../../lib/utils';

export default function ImageLogs() {
  const [logs, setLogs] = useState<ImageRequestLog[]>([]);
  const [keys, setKeys] = useState<ImageApiKey[]>([]);
  const [backends, setBackends] = useState<ImageBackend[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<ImageRequestLog | null>(null);

  // Filters
  const [filters, setFilters] = useState<ImageRequestFilters>({
    limit: 50,
    offset: 0,
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async () => {
    try {
      const [logsRes, keysRes, backendsRes] = await Promise.all([
        imageLogs.requests(filters),
        imageKeys.list(),
        imageBackends.list(),
      ]);
      setLogs(logsRes.requests);
      setTotal(logsRes.total);
      setKeys(keysRes.keys);
      setBackends(backendsRes.backends);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch logs';
      console.error('Failed to fetch logs:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const getKeyName = (keyId: string) => {
    const key = keys.find(k => k.id === keyId);
    return key?.name || keyId.slice(0, 8) + '...';
  };

  const getBackendName = (backendId: string | null) => {
    if (!backendId) return '-';
    const backend = backends.find(b => b.id === backendId);
    return backend?.name || backendId.slice(0, 8) + '...';
  };

  const handleFilterChange = (key: keyof ImageRequestFilters, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      offset: 0, // Reset pagination on filter change
    }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    const newOffset = direction === 'prev'
      ? Math.max(0, (filters.offset || 0) - (filters.limit || 50))
      : (filters.offset || 0) + (filters.limit || 50);
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1;
  const totalPages = Math.ceil(total / (filters.limit || 50));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading logs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">Image Logs</h1>
          <p className="text-muted-foreground mt-1">Image generation request history</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm',
              showFilters && 'bg-violet-500/20 text-violet-400'
            )}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={fetchData}
            className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400" />
            <div className="flex-1">
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">API Key</label>
              <select
                value={filters.imageKeyId || ''}
                onChange={(e) => handleFilterChange('imageKeyId', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm appearance-none cursor-pointer"
              >
                <option value="">All Keys</option>
                {keys.map(k => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Backend</label>
              <select
                value={filters.backendId || ''}
                onChange={(e) => handleFilterChange('backendId', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm appearance-none cursor-pointer"
              >
                <option value="">All Backends</option>
                {backends.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Start Date</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">End Date</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setFilters({ limit: 50, offset: 0 })}
              className="btn-secondary px-4 py-2 rounded-lg text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-100">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                API Key
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Backend
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Endpoint
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Response Time
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, index) => {
              const isSuccess = log.statusCode >= 200 && log.statusCode < 300;
              const isError = log.statusCode >= 400;

              return (
                <tr
                  key={log.id}
                  className="animate-fade-in-up hover:bg-secondary/30 cursor-pointer transition-colors"
                  style={{ animationDelay: `${(index + 2) * 30}ms` }}
                  onClick={() => setSelectedLog(log)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      {formatDateTime(log.timestamp)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm">{getKeyName(log.imageKeyId)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm">
                      <Server className="w-4 h-4 text-muted-foreground" />
                      {getBackendName(log.backendId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-xs font-mono bg-secondary/50 px-2 py-1 rounded">
                      {log.method} {log.endpoint}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {isSuccess ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      ) : isError ? (
                        <XCircle className="w-4 h-4 text-rose-400" />
                      ) : (
                        <Activity className="w-4 h-4 text-amber-400" />
                      )}
                      <span className={cn(
                        'text-sm font-mono',
                        isSuccess && 'text-emerald-400',
                        isError && 'text-rose-400'
                      )}>
                        {log.statusCode}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono">
                      {log.responseTimeMs ? `${(log.responseTimeMs / 1000).toFixed(2)}s` : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLog(log);
                      }}
                      className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-violet-400 transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {logs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <FileText className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">No logs found</p>
                      <p className="text-sm text-muted-foreground/70">Logs will appear here after image generation requests</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border/50">
            <div className="text-sm text-muted-foreground">
              Showing {(filters.offset || 0) + 1} - {Math.min((filters.offset || 0) + (filters.limit || 50), total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} getKeyName={getKeyName} getBackendName={getBackendName} />
      )}
    </div>
  );
}

function LogDetailModal({
  log,
  onClose,
  getKeyName,
  getBackendName,
}: {
  log: ImageRequestLog;
  onClose: () => void;
  getKeyName: (id: string) => string;
  getBackendName: (id: string | null) => string;
}) {
  const isSuccess = log.statusCode >= 200 && log.statusCode < 300;
  const workflowParams = log.workflowParams as unknown;

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4">
      <div
        className="modal-content rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center',
              isSuccess ? 'bg-emerald-500/10' : 'bg-rose-500/10'
            )}>
              {isSuccess ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : (
                <XCircle className="w-5 h-5 text-rose-400" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold">Request Details</h2>
              <p className="text-sm text-muted-foreground">{formatDateTime(log.timestamp)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">API Key</p>
            <p className="font-medium">{getKeyName(log.imageKeyId)}</p>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Backend</p>
            <p className="font-medium">{getBackendName(log.backendId)}</p>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Endpoint</p>
            <code className="text-sm font-mono">{log.method} {log.endpoint}</code>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Status Code</p>
            <p className={cn(
              'font-mono font-medium',
              isSuccess ? 'text-emerald-400' : 'text-rose-400'
            )}>
              {log.statusCode}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Response Time</p>
            <p className="font-mono">{log.responseTimeMs ? `${(log.responseTimeMs / 1000).toFixed(3)}s` : '-'}</p>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Device ID</p>
            <p className="font-mono text-sm truncate" title={log.deviceId || '-'}>
              {log.deviceId || '-'}
            </p>
          </div>
        </div>

        {/* Prompt ID */}
        {log.promptId && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-2">Prompt ID</p>
            <code className="block w-full p-4 rounded-xl bg-secondary/30 font-mono text-sm break-all">
              {log.promptId}
            </code>
          </div>
        )}

        {/* Error Message */}
        {log.errorMessage && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-2">Error Message</p>
            <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
              <p className="text-sm text-rose-300">{log.errorMessage}</p>
            </div>
          </div>
        )}

        {/* Workflow Params */}
        {workflowParams !== null && workflowParams !== undefined && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">Workflow Parameters</p>
            <pre className="p-4 rounded-xl bg-secondary/30 font-mono text-xs overflow-x-auto max-h-64">
              {JSON.stringify(workflowParams, null, 2)}
            </pre>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mt-6 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="btn-secondary px-5 py-2.5 rounded-xl text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
